import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, ArrowLeft, Image, ChevronDown, Lock, LogOut } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'qualiance_admin_token';

const authHeader = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const COMPLEXITY_OPTIONS = ['Low', 'Medium', 'Moderate', 'High', 'Very High', 'Highest'];
const CATEGORY_OPTIONS = ['Technical Outerwear', 'Workwear', 'Corporate Wear', 'Activewear & Performance', 'Sustainable Childrenswear', 'High Fashion'];

const LoginScreen = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#1C1C1E' }} data-testid="admin-login">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="font-display text-2xl font-bold" style={{ color: '#C8102E' }}>Q</span>
          <div>
            <div className="font-display text-lg text-white">Admin Access</div>
            <div className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/30">Qualiance International</div>
          </div>
        </div>
        <form onSubmit={handleLogin} className="space-y-4 bg-white/5 border border-white/10 p-8">
          <div className="flex items-center gap-2 text-white/40 mb-2">
            <Lock size={14} />
            <span className="font-mono text-[10px] tracking-[0.15em] uppercase">Restricted Area</span>
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
              placeholder="admin@qualiance.com"
              required
            />
          </div>
          <div>
            <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
              placeholder="••••••••"
              required
            />
          </div>
          {error && <p className="text-[#C41E3A] text-xs font-mono" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full flex items-center justify-center gap-2 bg-[#C41E3A] text-white text-xs font-mono uppercase tracking-wider px-5 py-3 hover:bg-[#a01830] transition-colors disabled:opacity-50"
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
          <Link to="/" className="block text-center font-mono text-[10px] text-white/30 hover:text-white/60 transition-colors pt-2">
            ← Back to Site
          </Link>
        </form>
      </motion.div>
    </div>
  );
};

const AdminPanel = () => {
  const [authed, setAuthed] = useState(null);
  const [products, setProducts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('products');
  const [formData, setFormData] = useState({
    category: 'Technical Outerwear',
    subcategory: '',
    name: '',
    description: '',
    image_url: '',
    specs: {},
    tags: [],
    complexity: 'High',
    order: 0,
  });
  const [specsText, setSpecsText] = useState('');
  const [tagsText, setTagsText] = useState('');

  useEffect(() => {
    const verify = async () => {
      if (!localStorage.getItem(TOKEN_KEY)) { setAuthed(false); return; }
      try {
        await axios.get(`${API}/auth/me`, { headers: authHeader() });
        setAuthed(true);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }
    };
    verify();
  }, []);

  useEffect(() => {
    if (authed) {
      fetchProducts();
      fetchContacts();
    }
  }, [authed]);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
  };

  const handleAuthError = (e) => {
    if (e.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/contact`, { headers: authHeader() });
      setContacts(res.data);
    } catch (e) { handleAuthError(e); console.error(e); }
  };

  const openNew = () => {
    setEditingProduct(null);
    setFormData({ category: 'Technical Outerwear', subcategory: '', name: '', description: '', image_url: '', specs: {}, tags: [], complexity: 'High', order: products.length + 1 });
    setSpecsText('');
    setTagsText('');
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      category: product.category,
      subcategory: product.subcategory,
      name: product.name,
      description: product.description,
      image_url: product.image_url,
      specs: product.specs || {},
      tags: product.tags || [],
      complexity: product.complexity,
      order: product.order,
    });
    setSpecsText(Object.entries(product.specs || {}).map(([k, v]) => `${k}: ${v}`).join('\n'));
    setTagsText((product.tags || []).join(', '));
    setShowForm(true);
  };

  const handleSave = async () => {
    const specs = {};
    specsText.split('\n').filter(Boolean).forEach(line => {
      const idx = line.indexOf(':');
      if (idx > 0) {
        specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
      }
    });
    const tags = tagsText.split(',').map(t => t.trim()).filter(Boolean);
    const payload = { ...formData, specs, tags };

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, { headers: authHeader() });
      } else {
        await axios.post(`${API}/products`, payload, { headers: authHeader() });
      }
      fetchProducts();
      setShowForm(false);
    } catch (e) { handleAuthError(e); console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers: authHeader() });
      fetchProducts();
    } catch (e) { handleAuthError(e); console.error(e); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, { headers: authHeader() });
      setFormData(prev => ({ ...prev, image_url: `${process.env.REACT_APP_BACKEND_URL}${res.data.url}` }));
    } catch (err) { handleAuthError(err); console.error(err); }
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1C1C1E' }} data-testid="admin-loading">
        <span className="font-mono text-xs text-white/40 tracking-[0.15em] uppercase">Verifying access...</span>
      </div>
    );
  }

  if (!authed) {
    return <LoginScreen onSuccess={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen text-white" style={{ background: '#1C1C1E' }} data-testid="admin-panel">
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 transition-colors" style={{ color: 'rgba(255,255,255,0.5)' }} data-testid="admin-back">
              <ArrowLeft size={16} />
              <span className="font-mono text-xs">Back to Site</span>
            </Link>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
            <span className="font-display text-lg text-white">Admin Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 hover:text-white transition-colors">
              <LogOut size={13} /> Logout
            </button>
            <span style={{ color: 'rgba(255,255,255,0.2)' }}>|</span>
            <span className="font-display text-sm font-bold" style={{ color: '#C8102E' }}>Q</span>
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8">
          {['products', 'contacts'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              data-testid={`admin-tab-${tab}`}
              className={`font-mono text-xs tracking-[0.15em] uppercase px-6 py-3 transition-colors ${
                activeTab === tab ? 'bg-[#C41E3A] text-white' : 'bg-white/5 text-white/40 hover:text-white'
              }`}
            >
              {tab} ({tab === 'products' ? products.length : contacts.length})
            </button>
          ))}
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl">Hero Products</h2>
              <button
                onClick={openNew}
                data-testid="admin-add-product"
                className="flex items-center gap-2 bg-[#C41E3A] text-white text-xs font-mono uppercase tracking-wider px-4 py-2.5 hover:bg-[#a01830] transition-colors"
              >
                <Plus size={14} /> Add Product
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full admin-table" data-testid="admin-products-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-white/40">Order</th>
                    <th className="text-left py-3 px-4 text-white/40">Image</th>
                    <th className="text-left py-3 px-4 text-white/40">Name</th>
                    <th className="text-left py-3 px-4 text-white/40">Category</th>
                    <th className="text-left py-3 px-4 text-white/40">Complexity</th>
                    <th className="text-right py-3 px-4 text-white/40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, i) => (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`admin-product-row-${i}`}>
                      <td className="py-3 px-4 font-mono text-xs text-white/40">{p.order}</td>
                      <td className="py-3 px-4">
                        {p.image_url && (
                          <img src={p.image_url} alt={p.name} className="w-10 h-10 object-cover rounded-sm" />
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-white">{p.name}</td>
                      <td className="py-3 px-4 font-mono text-[10px] text-white/40 uppercase">{p.category}</td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-[10px] px-2 py-1 bg-[#C41E3A]/10 text-[#C41E3A]">{p.complexity}</span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => openEdit(p)} className="text-white/40 hover:text-white mr-3" data-testid={`admin-edit-${i}`}>
                          <Edit2 size={14} />
                        </button>
                        <button onClick={() => handleDelete(p.id)} className="text-white/40 hover:text-[#C41E3A]" data-testid={`admin-delete-${i}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <>
            <h2 className="font-heading text-2xl mb-6">Contact Submissions</h2>
            <div className="space-y-3">
              {contacts.map((c, i) => (
                <div key={c.id} className="bg-white/5 border border-white/5 p-6" data-testid={`admin-contact-${i}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.15em] uppercase px-2 py-1 bg-[#C41E3A]/10 text-[#C41E3A]">{c.type}</span>
                      <h3 className="text-white font-medium mt-2">{c.name}</h3>
                      <p className="text-white/40 text-xs">{c.email} {c.company && `— ${c.company}`}</p>
                    </div>
                    <span className="font-mono text-[10px] text-white/20">{new Date(c.created_at).toLocaleDateString()}</span>
                  </div>
                  {c.message && <p className="text-white/50 text-sm">{c.message}</p>}
                </div>
              ))}
              {contacts.length === 0 && <p className="text-white/30 text-sm">No submissions yet.</p>}
            </div>
          </>
        )}
      </div>

      {/* Product Form Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8"
            onClick={() => setShowForm(false)}
          >
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className="bg-[#18181b] border border-white/10 w-full max-w-2xl mx-4"
              onClick={(e) => e.stopPropagation()}
              data-testid="admin-product-form"
            >
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h3 className="font-heading text-xl">{editingProduct ? 'Edit Product' : 'Add New Product'}</h3>
                <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white">
                  <X size={18} />
                </button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Category</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      data-testid="form-category"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                    >
                      {CATEGORY_OPTIONS.map(c => <option key={c} value={c} className="bg-[#18181b]">{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Subcategory</label>
                    <input
                      value={formData.subcategory}
                      onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                      data-testid="form-subcategory"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                      placeholder="e.g., Technical Shell"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Product Name</label>
                  <input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="form-name"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                    placeholder="e.g., 3-Layer Waterproof Hardshell"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="form-description"
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none resize-none"
                    placeholder="Detailed product description..."
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-2">Product Image</label>
                  <div className="flex gap-3">
                    <input
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      data-testid="form-image-url"
                      className="flex-1 bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                      placeholder="Image URL or upload below"
                    />
                    <label className="flex items-center gap-2 bg-white/5 border border-white/10 px-4 py-2.5 cursor-pointer hover:bg-white/10 transition-colors">
                      <Image size={14} className="text-white/40" />
                      <span className="font-mono text-[10px] text-white/60">Upload</span>
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="form-image-upload" />
                    </label>
                  </div>
                  {formData.image_url && (
                    <img src={formData.image_url} alt="Preview" className="mt-2 h-20 object-cover rounded-sm" />
                  )}
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">
                    Tech Specs <span className="text-white/20">(one per line, Key: Value)</span>
                  </label>
                  <textarea
                    value={specsText}
                    onChange={(e) => setSpecsText(e.target.value)}
                    data-testid="form-specs"
                    rows={4}
                    className="w-full bg-white/5 border border-white/10 text-white font-mono text-xs px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none resize-none"
                    placeholder="Shell Fabric: 3L Nylon Ripstop&#10;Seam Technology: Fully taped seams&#10;Zippers: YKK AquaGuard"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">
                    Tags <span className="text-white/20">(comma separated)</span>
                  </label>
                  <input
                    value={tagsText}
                    onChange={(e) => setTagsText(e.target.value)}
                    data-testid="form-tags"
                    className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                    placeholder="Waterproof, DryBond, 3-Layer"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Complexity</label>
                    <select
                      value={formData.complexity}
                      onChange={(e) => setFormData({ ...formData, complexity: e.target.value })}
                      data-testid="form-complexity"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                    >
                      {COMPLEXITY_OPTIONS.map(c => <option key={c} value={c} className="bg-[#18181b]">{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.15em] uppercase text-white/40 block mb-1">Display Order</label>
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      data-testid="form-order"
                      className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 focus:border-[#C41E3A]/50 focus:outline-none"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
                <button
                  onClick={() => setShowForm(false)}
                  className="font-mono text-xs text-white/40 px-4 py-2.5 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  data-testid="form-save"
                  className="flex items-center gap-2 bg-[#C41E3A] text-white text-xs font-mono uppercase tracking-wider px-5 py-2.5 hover:bg-[#a01830] transition-colors"
                >
                  <Save size={14} /> Save Product
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
